/**
 * Onda Sistema de Luz — the FakeLight system (client/render-only, ZERO wire, ZERO sim impact).
 *
 * "Estado da arte" on a mobile budget means CHEAP, not dynamic: there is deliberately no THREE.PointLight
 * per lamp (each would force per-fragment lighting on every lit material — a mobile killer). Instead every
 * light source in the sea world — ship stern/deck lanterns, port street lamps, building windows, bonfires,
 * the lighthouse beacon, and any light the owner drops in the Landmark editor — is ONE entry in this
 * registry, drawn by three cooperating cheap techniques:
 *
 *   1. GLOW SPRITES, INSTANCED — every visible light in the world renders as one billboard quad in a single
 *      InstancedMesh (additive blending, procedural radial texture): the whole night fleet + every port
 *      TOGETHER cost exactly 1 draw call. Day (`nightFactor` ≈ 0) sets count 0 → literally zero GPU cost.
 *   2. OCEAN GLINTS — the N brightest/nearest water-reflecting lights are handed to the ocean shader as
 *      uniforms each frame (see render.ts buildOceanMaterial); the fragment shader draws a trembling
 *      specular smear on the waves under each lantern. Pure uniforms + ALU: no extra draw, no RTT.
 *   3. NIGHT EMISSIVES — materials registered here (e.g. the lighthouse lantern room) get their
 *      emissiveIntensity lerped with the day/night cycle, so windows/lamps "acendem" in the mesh itself.
 *
 * Flicker is deterministic: each light derives its phase from a caller-supplied integer seed (ship id,
 * landmark position hash, …) via the shared mulberry32 — same world, same flame dance on every client,
 * and the render clock never touches the sim.
 *
 * Lifecycle is self-healing: every light is anchored to a scene Object3D (a ship group, an outpost group,
 * a base jetty). A light whose anchor left the scene graph (ship sank/despawned, setWorld rebuilt the
 * ports, base removed) is pruned automatically on the next update — no removal bookkeeping at any call
 * site, no leak possible. An anchor that is merely `visible = false` (distance-culled port, LOD) keeps its
 * light but skips it — culling for free.
 *
 * Per-frame work is allocation-free: scratch vectors, fixed-size uniform arrays, insertion-select for the
 * glint slots (no sort, no filter, no closures).
 */
import * as THREE from 'three';
import { mulberry32 } from '@piratas/sim';

/* ────────────────────────────────────────── presets ────────────────────────────────────────── */

export type FlickerKind = 'none' | 'candle' | 'lantern' | 'torch' | 'bonfire' | 'window' | 'beacon';

export interface LightPreset {
  /** glow tint (0xRRGGBB) */
  color: number;
  /** brightness budget 0..~1.2 (multiplies the glow alpha + the ocean-glint strength) */
  intensity: number;
  /** glow radius in world metres (the billboard is ~2× this across); also scales the glint pool */
  radius: number;
  flicker: FlickerKind;
  /** does this light reflect on the water (compete for one of the N ocean-glint slots)? */
  glint: boolean;
  /** default height (m) above the anchor point — used by the pure `light_*` landmark kinds */
  y: number;
}

/** The preset table — everything player-visible about a light type lives HERE (data, not code).
 *  The `light_*` Landmark-editor kinds map 1:1 onto these by name (`light_lantern` → `lantern`). */
export const LIGHT_PRESETS = {
  /** warm ship/port lantern — the workhorse */
  lantern: { color: 0xffb347, intensity: 0.9, radius: 3.2, flicker: 'lantern' as FlickerKind, glint: true, y: 4.2 },
  /** small soft candle — cabin corners, small props */
  candle: { color: 0xffa54f, intensity: 0.55, radius: 2.0, flicker: 'candle' as FlickerKind, glint: false, y: 2.2 },
  /** a lit window pane — near-steady warm interior glow */
  window: { color: 0xffd591, intensity: 0.6, radius: 2.2, flicker: 'window' as FlickerKind, glint: false, y: 3.0 },
  /** a hand torch / watch-fire — livelier, redder flame */
  torch: { color: 0xff9a3c, intensity: 0.8, radius: 2.6, flicker: 'torch' as FlickerKind, glint: false, y: 3.2 },
  /** a big open bonfire — deep chaotic flicker, reads across the bay */
  bonfire: { color: 0xff8a2a, intensity: 1.2, radius: 6.0, flicker: 'bonfire' as FlickerKind, glint: true, y: 1.6 },
  /** the lighthouse lamp room — slow lens pulse, far reach, strong water reflection */
  beacon: { color: 0xffe9b0, intensity: 1.15, radius: 7.0, flicker: 'beacon' as FlickerKind, glint: true, y: 29 },
} as const satisfies Record<string, LightPreset>;

export type LightPresetName = keyof typeof LIGHT_PRESETS;

/** Landmark kind → preset for the pure-light editor kinds. Open both ways (unknown → null). */
export function presetForLightKind(kind: string): LightPreset | null {
  if (!kind.startsWith('light_')) return null;
  const p = (LIGHT_PRESETS as Record<string, LightPreset>)[kind.slice('light_'.length)];
  return p ?? null;
}

/* ─────────────────────────────────────── pure flame math ───────────────────────────────────────
 * All pure + deterministic (unit-tested): flicker waveforms, the dusk/dawn ignition ramp, phases. */

/** Deterministic flicker phase from an integer seed (shared mulberry32 → same on every client). */
export function phaseFromSeed(seed: number): number {
  return mulberry32(Math.floor(seed) >>> 0)() * 100;
}

/** Flame flutter multiplier at time t for a light with phase ph. Bounded, allocation-free, pure. */
export function flickerAt(kind: FlickerKind, t: number, ph: number): number {
  switch (kind) {
    case 'none': return 1;
    case 'candle': return 0.82 + 0.18 * (0.6 * Math.sin(t * 9 + ph) + 0.4 * Math.sin(t * 17 + ph * 1.7));
    case 'lantern': return 0.86 + 0.14 * (0.6 * Math.sin(t * 6 + ph) + 0.4 * Math.sin(t * 13 + ph * 1.7));
    case 'torch': return 0.72 + 0.28 * (0.55 * Math.sin(t * 11 + ph) + 0.45 * Math.sin(t * 23 + ph * 2.1));
    case 'bonfire': return 0.58 + 0.42 * (0.5 * Math.sin(t * 7 + ph) + 0.3 * Math.sin(t * 19 + ph * 2.3) + 0.2 * Math.sin(t * 31 + ph * 0.7));
    case 'window': return 0.94 + 0.06 * Math.sin(t * 3 + ph);
    case 'beacon': { const s = 0.5 + 0.5 * Math.sin(t * 1.3 + ph); return 0.42 + 0.58 * s * s * s; } // slow lens sweep
  }
}

const smooth01 = (x: number): number => { const k = Math.min(1, Math.max(0, x)); return k * k * (3 - 2 * k); };

/** Dusk/dawn ignition ramp: 0 in daylight → 1 deep at night, with a small per-light threshold jitter so
 *  the lamps of a port ignite one by one across the dusk instead of all popping on the same frame. */
export function litAmount(nightFactor: number, ph: number): number {
  const jitter = 0.06 * (ph * 0.618 - Math.floor(ph * 0.618)); // deterministic 0..0.06 from the phase
  const on = 0.04 + jitter;
  return smooth01((nightFactor - on) / 0.20);
}

/* ─────────────────────────────────────── ship light rigs ───────────────────────────────────────
 * Where the lanterns hang on each hull CLASS — DATA, in hull-template-local metres (the base ~20 m
 * brigantine hull, bow at +X, waterline y=0; the ship group's ×classScale sizes it per class, exactly
 * like the helm/crew/cannon tables in render.ts). `sway` = gentle hanging-lamp swing amplitude (m). */

export interface ShipLightAnchor { x: number; y: number; z: number; preset: LightPresetName; r?: number; i?: number; sway?: number; glint?: boolean }

const B = 3.5; // half-beam of the base hull (SHIP.BEAM / 2) — kept local so this table stays pure data
/** The classic lantern rig every hull carries (matches the retired sprite lanterns pixel-for-pixel):
 *  a big stern poop lantern (the one that owns a water glint), two stern quarter lights, a small bow
 *  light and two mid-deck side lamps hanging off the rails (low over the water → they glint too). */
const RIG_BASE: ShipLightAnchor[] = [
  { x: -8.0, y: 6.6, z: 0, preset: 'lantern', r: 2.0, i: 0.9, sway: 0.10, glint: true },   // stern main
  { x: -6.2, y: 4.7, z: -B * 0.7, preset: 'candle', r: 1.1, i: 0.5, sway: 0.18 },          // stern quarter (port)
  { x: -6.2, y: 4.7, z: B * 0.7, preset: 'candle', r: 1.1, i: 0.5, sway: 0.18 },           // stern quarter (starboard)
  { x: 8.4, y: 4.0, z: 0, preset: 'candle', r: 1.2, i: 0.48, sway: 0.10 },                 // bow
  { x: 0, y: 4.5, z: -B * 0.96, preset: 'lantern', r: 1.45, i: 0.66, sway: 0.30, glint: true }, // mid port rail
  { x: 0, y: 4.5, z: B * 0.96, preset: 'lantern', r: 1.45, i: 0.66, sway: 0.30, glint: true },  // mid starboard rail
];
/** The tall three-masters additionally fly a masthead light below the flag (~y 31 template-local). */
const RIG_MASTHEAD: ShipLightAnchor = { x: -2, y: 24, z: 0, preset: 'candle', r: 1.3, i: 0.55, sway: 0.22 };

/** Per-ShipClassId rig (0 sloop · 1 brigantine · 2 galleon · 3 frigate · 4 cutter · 5 fluyt · 6 east indiaman).
 *  Unknown class → RIG_BASE. Big hulls (galleon/frigate/east indiaman) add the masthead light; the flagship
 *  East Indiaman also lights her stern gallery (two extra candles) — "a wall of lights" to match her guns. */
export const SHIP_LIGHT_RIGS: Record<number, ShipLightAnchor[]> = {
  0: RIG_BASE, 1: RIG_BASE, 4: RIG_BASE, 5: RIG_BASE,
  2: [...RIG_BASE, RIG_MASTHEAD],
  3: [...RIG_BASE, RIG_MASTHEAD],
  6: [...RIG_BASE, RIG_MASTHEAD,
    { x: -8.6, y: 5.6, z: -B * 0.5, preset: 'candle', r: 1.0, i: 0.45, sway: 0.12 },
    { x: -8.6, y: 5.6, z: B * 0.5, preset: 'candle', r: 1.0, i: 0.45, sway: 0.12 }],
};

export function shipLightRig(cls: number): ShipLightAnchor[] { return SHIP_LIGHT_RIGS[cls] ?? RIG_BASE; }

/* ────────────────────────────────────────── registry ────────────────────────────────────────── */

export interface AddLightOpts {
  /** scene object the light rides (ship group, outpost group, jetty…). Pruned when it leaves the scene. */
  anchor: THREE.Object3D;
  /** anchor-local offset (three local units of the anchor — for a scaled ship group, hull-template units) */
  lx: number; ly: number; lz: number;
  preset: LightPresetName | LightPreset;
  /** integer seed for the deterministic flicker phase (ship id, position hash, …) */
  seed: number;
  /** multiplies the preset radius (e.g. a landmark piece's `scale`) */
  radiusScale?: number;
  /** multiplies the preset intensity */
  intensityScale?: number;
  /** 0xRRGGBB colour override (the editor's custom tint) */
  tint?: number;
  /** hanging-lamp swing amplitude in ANCHOR-LOCAL units (0 = rigid) */
  sway?: number;
  /** override the preset's water-glint participation */
  glint?: boolean;
}

interface FakeLight {
  anchor: THREE.Object3D;
  lx: number; ly: number; lz: number;
  color: THREE.Color;
  intensity: number;
  radius: number;
  flicker: FlickerKind;
  phase: number;
  glint: boolean;
  sway: number;
  // per-frame computed world position (valid only while lit; reused by the glint pass)
  wx: number; wy: number; wz: number;
  out: number; // current brightness (intensity × flicker × lit), 0 when dark/culled
}

/** How many lights can glow on screen at once (hard cap on the instanced batch — beyond it the farthest
 *  adds are simply skipped that frame). 40 AOI ships × ~6 + a decorated port fits comfortably. */
const MAX_GLOWS = 256;
/** Ocean-glint uniform slots (N nearest reflecting lights) — mirrored by GLINT_MAX in render.ts's shader. */
export const GLINT_MAX = 8;
/** Beyond this (m² from the camera) a light neither glows nor glints — subpixel anyway. */
const LIGHT_CULL_D2 = 750 * 750;
/** Below this nightFactor the whole system is asleep (count 0, no per-light work beyond one branch). */
const WAKE_NF = 0.02;

export class LightSystem {
  private lights: FakeLight[] = [];
  private mesh: THREE.InstancedMesh;
  private readonly mat: THREE.MeshBasicMaterial;
  // glint outputs (fixed slots, alloc-free) — read by writeGlints
  private glintD2 = new Array<number>(GLINT_MAX).fill(Infinity);
  private glintLight = new Array<FakeLight | null>(GLINT_MAX).fill(null);
  private glintCount = 0;
  // night-emissive registrations (windows/lamp rooms baked into meshes)
  private emissives: { mat: THREE.MeshStandardMaterial; anchor: THREE.Object3D; day: number; night: number }[] = [];
  // scratch (per-frame, allocation-free)
  private readonly _v = new THREE.Vector3();
  private readonly _s = new THREE.Vector3();
  private readonly _m = new THREE.Matrix4();
  private readonly _c = new THREE.Color();
  private _lastAnchor: THREE.Object3D | null = null;

  constructor(scene: THREE.Scene, glowTex: THREE.Texture) {
    // one shared billboard quad; per-instance matrix (position+camera-facing+scale) and colour
    // (tint × brightness — additive blending, so colour IS the alpha).
    const geo = new THREE.PlaneGeometry(2, 2);
    this.mat = new THREE.MeshBasicMaterial({
      map: glowTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false,
    });
    this.mesh = new THREE.InstancedMesh(geo, this.mat, MAX_GLOWS);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_GLOWS * 3), 3);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.frustumCulled = false; // we distance-cull per light; the batch spans the whole view
    this.mesh.renderOrder = 8;       // over the water + hulls, under the chest beacons (9)
    scene.add(this.mesh);
  }

  /** Register a light. No handle needed — the light dies automatically when its anchor leaves the scene. */
  add(o: AddLightOpts): void {
    const p = typeof o.preset === 'string' ? LIGHT_PRESETS[o.preset] : o.preset;
    this.lights.push({
      anchor: o.anchor, lx: o.lx, ly: o.ly, lz: o.lz,
      color: new THREE.Color(o.tint ?? p.color),
      intensity: p.intensity * (o.intensityScale ?? 1),
      radius: p.radius * (o.radiusScale ?? 1),
      flicker: p.flicker,
      phase: phaseFromSeed(o.seed),
      glint: o.glint ?? p.glint,
      sway: o.sway ?? 0,
      wx: 0, wy: 0, wz: 0, out: 0,
    });
  }

  /** Convenience: hang a hull class's full lantern rig on a ship group (hull-template-local units —
   *  the group's ×classScale scales positions AND radii/sway, so a galleon's lamps read bigger). */
  addShipLights(shipGroup: THREE.Object3D, cls: number, shipId: number, groupScale: number): void {
    const rig = shipLightRig(cls);
    for (let i = 0; i < rig.length; i++) {
      const a = rig[i]!;
      const pr = LIGHT_PRESETS[a.preset];
      this.add({
        anchor: shipGroup, lx: a.x, ly: a.y, lz: a.z, preset: a.preset,
        seed: (shipId | 0) * 31 + i,
        radiusScale: ((a.r ?? pr.radius) / pr.radius) * groupScale,
        intensityScale: (a.i ?? pr.intensity) / pr.intensity,
        sway: a.sway ?? 0, glint: a.glint ?? false,
      });
    }
  }

  /** Register a mesh material whose emissive should breathe with the night (window panes, lamp rooms,
   *  the foundry furnace). Pruned with its anchor like any light. We mutate emissiveIntensity in place,
   *  so the material must either be OWNED by the caller, or be a GLB-TEMPLATE material registered exactly
   *  ONCE with a permanent anchor (the scene) — then every clone sharing it breathes in unison. */
  registerEmissive(mat: THREE.MeshStandardMaterial, anchor: THREE.Object3D, day: number, night: number): void {
    this.emissives.push({ mat, anchor, day, night });
  }

  /** True when the object is still connected to the scene graph root we render. */
  private static attached(o: THREE.Object3D): boolean {
    let p: THREE.Object3D | null = o;
    while (p.parent) p = p.parent;
    return (p as unknown as { isScene?: boolean }).isScene === true;
  }
  /** True when the object or any ancestor is hidden (distance-culled port, LOD swap, wreck…). */
  private static hidden(o: THREE.Object3D): boolean {
    for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return true;
    return false;
  }

  /** Per-frame drive. Call once per rendered sea frame with the render clock, the day/night factor and
   *  the camera. Fills the instanced glow batch + selects the ocean-glint slots. Allocation-free. */
  update(t: number, nightFactor: number, cam: THREE.Vector3): void {
    // prune dead anchors (swap-remove — order is irrelevant)
    const L = this.lights;
    for (let i = L.length - 1; i >= 0; i--) {
      if (!LightSystem.attached(L[i]!.anchor)) { L[i] = L[L.length - 1]!; L.pop(); }
    }
    for (let i = this.emissives.length - 1; i >= 0; i--) {
      if (!LightSystem.attached(this.emissives[i]!.anchor)) { this.emissives[i] = this.emissives[this.emissives.length - 1]!; this.emissives.pop(); }
    }
    // emissives breathe with the cycle (a handful of materials — cheap even by day)
    const eNf = smooth01((nightFactor - 0.03) / 0.22);
    for (let i = 0; i < this.emissives.length; i++) {
      const e = this.emissives[i]!;
      e.mat.emissiveIntensity = e.day + (e.night - e.day) * eNf;
    }
    this.glintCount = 0;
    if (nightFactor <= WAKE_NF || L.length === 0) { // broad daylight → the system sleeps
      if (this.mesh.count) { this.mesh.count = 0; }
      this.mesh.visible = false;
      return;
    }
    for (let i = 0; i < GLINT_MAX; i++) { this.glintD2[i] = Infinity; this.glintLight[i] = null; }
    const v = this._v, s = this._s, m = this._m, c = this._c;
    let n = 0;
    this._lastAnchor = null;
    for (let i = 0; i < L.length; i++) {
      const l = L[i]!;
      const a = l.anchor;
      if (LightSystem.hidden(a)) { l.out = 0; continue; }
      // refresh the anchor's world matrix once per contiguous run (ship rigs add their lights together)
      if (a !== this._lastAnchor) { a.updateWorldMatrix(true, false); this._lastAnchor = a; }
      v.set(l.lx, l.ly, l.lz).applyMatrix4(a.matrixWorld);
      if (l.sway > 0) { // gentle hanging-lamp swing (world-space, scaled by the anchor's uniform scale)
        const sc = this._s.setFromMatrixScale(a.matrixWorld).x;
        v.x += Math.sin(t * 1.6 + l.phase) * l.sway * sc;
        v.z += Math.cos(t * 1.2 + l.phase) * l.sway * sc * 0.7;
      }
      const dx = v.x - cam.x, dz = v.z - cam.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > LIGHT_CULL_D2) { l.out = 0; continue; }
      const lit = litAmount(nightFactor, l.phase);
      if (lit <= 0.001) { l.out = 0; continue; }
      const fk = flickerAt(l.flicker, t, l.phase);
      const out = l.intensity * fk * lit;
      if (out <= 0.01) { l.out = 0; continue; }
      l.out = out; l.wx = v.x; l.wy = v.y; l.wz = v.z;
      // glint slot contest: keep the GLINT_MAX nearest reflecting lights (replace the current farthest)
      if (l.glint) {
        let worst = 0;
        for (let k = 1; k < GLINT_MAX; k++) if (this.glintD2[k]! > this.glintD2[worst]!) worst = k;
        if (d2 < this.glintD2[worst]!) { this.glintD2[worst] = d2; this.glintLight[worst] = l; }
      }
      if (n >= MAX_GLOWS) continue; // batch full — farthest adds simply skip the glow this frame
      // billboard: camera-facing quad, scale = glow diameter, breathing slightly with the flame
      s.setScalar(l.radius * (1.55 + 0.35 * fk));
      m.compose(v, this.cameraQuat, s);
      this.mesh.setMatrixAt(n, m);
      // additive blending: colour × brightness IS the light (no per-instance opacity needed)
      c.copy(l.color).multiplyScalar(Math.min(1.25, out) * 0.62);
      this.mesh.setColorAt(n, c);
      n++;
    }
    let gc = 0; // compact the glint slots (drop empty ones)
    for (let k = 0; k < GLINT_MAX; k++) if (this.glintLight[k]) { this.glintLight[gc] = this.glintLight[k]!; this.glintD2[gc] = this.glintD2[k]!; gc++; }
    for (let k = gc; k < GLINT_MAX; k++) this.glintLight[k] = null;
    this.glintCount = gc;
    this.mesh.count = n;
    this.mesh.visible = n > 0;
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.mesh.instanceColor!.needsUpdate = true;
    }
  }

  private readonly cameraQuat = new THREE.Quaternion();
  /** Feed the camera's orientation for billboard alignment (call right before update()). */
  setCameraQuaternion(q: THREE.Quaternion): void { this.cameraQuat.copy(q); }

  /** Push the selected water-reflecting lights into the ocean shader's uniforms.
   *  uGlint[i] = (worldX, worldZ, lampHeightY, strength); uGlintCol[i] = linear tint. */
  writeGlints(oceanUniforms: Record<string, THREE.IUniform>): void {
    const cnt = oceanUniforms.uGlintCnt, arr = oceanUniforms.uGlint, col = oceanUniforms.uGlintCol;
    if (!cnt || !arr || !col) return; // a foreign ocean material (e.g. an old snapshot) — no-op
    const va = arr.value as THREE.Vector4[], vc = col.value as THREE.Color[];
    for (let i = 0; i < this.glintCount; i++) {
      const l = this.glintLight[i]!;
      va[i]!.set(l.wx, l.wz, l.wy, l.out * (0.6 + 0.4 * Math.min(1, l.radius / 4)));
      vc[i]!.copy(l.color);
    }
    cnt.value = this.glintCount;
  }

  /** How many lights are registered (tests/debug). */
  get size(): number { return this.lights.length; }
  /** Current on-screen glow count (tests/debug/perf HUD). */
  get glowCount(): number { return this.mesh.count; }
  /** Current ocean-glint slot usage (tests/debug). */
  get glints(): number { return this.glintCount; }
}

/* ─────────────────────────── landmark piece → lights (sea-side data) ───────────────────────────
 * Which lights each PLACEABLE LANDMARK KIND carries, in PIECE-LOCAL metres (rotated by the piece's
 * yaw, scaled by its `scale`, lifted by its `h`). The window/lamp offsets come straight from the
 * walkable city's own night tables (portscene WINDOW_SPECS / LAMP_KINDS) so the two scenes can never
 * drift; the lighthouse beacon + wharf lantern are sea-only heroes. */

export interface KindLight { dx: number; dy: number; dz: number; preset: LightPresetName; r?: number; i?: number; glint?: boolean }

/** Build the kind→lights table from the walkable-city night data (call once with portscene's tables). */
export function buildLandmarkKindLights(
  windowSpecs: Record<string, { dx: number; dz: number; up: number }[]>,
  lampKinds: Record<string, { headY: number }>,
): Record<string, KindLight[]> {
  const out: Record<string, KindLight[]> = {};
  for (const [kind, specs] of Object.entries(windowSpecs)) {
    out[kind] = specs.map((w) => ({ dx: w.dx, dy: w.up, dz: w.dz, preset: 'window' as LightPresetName }));
  }
  for (const [kind, lamp] of Object.entries(lampKinds)) {
    (out[kind] ??= []).push({ dx: 0, dy: lamp.headY, dz: 0, preset: 'lantern', r: 0.7, glint: true });
  }
  // sea-only heroes: the lighthouse lamp room (top of the 30 m tower) + a wharf dock lantern
  out.lighthouse = [{ dx: 0, dy: 29, dz: 0, preset: 'beacon', glint: true }];
  out.wharf_skullflag = [{ dx: 0, dy: 5, dz: 0, preset: 'lantern', r: 0.8, glint: true }];
  return out;
}
